import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { bindAuthInterceptor, getMyPermissions } from "../api/permissionApi";
import { isAdminUser } from "../utils/jwtRole";

const PermissionContext = createContext(null);

function normalizeScreens(screens) {
  const map = {};
  (screens || []).forEach((item) => {
    const code = item.screenCode || item.ScreenCode;
    if (!code) return;
    map[code] = {
      screenCode: code,
      screenName: item.screenName || item.ScreenName || code,
      parentCode: item.parentCode || item.ParentCode || null,
      routePath: item.routePath || item.RoutePath || "",
      canView: !!(item.canView ?? item.CanView),
      canCreate: !!(item.canCreate ?? item.CanCreate),
      canEdit: !!(item.canEdit ?? item.CanEdit),
      canDelete: !!(item.canDelete ?? item.CanDelete),
      supportsCreate: !!(item.supportsCreate ?? item.SupportsCreate),
      supportsEdit: !!(item.supportsEdit ?? item.SupportsEdit),
      supportsDelete: !!(item.supportsDelete ?? item.SupportsDelete),
    };
  });
  return map;
}

export function PermissionProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(isAdminUser());
  const [roles, setRoles] = useState([]);
  const [screenMap, setScreenMap] = useState({});

  const reload = useCallback(async () => {
    bindAuthInterceptor();
    const token =
      localStorage.getItem("sundorbonToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("sundorbonToken");

    if (!token) {
      setIsAdmin(false);
      setRoles([]);
      setScreenMap({});
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const mine = await getMyPermissions();
      setIsAdmin(!!(mine.isAdmin ?? mine.IsAdmin) || isAdminUser());
      setRoles(mine.roles || mine.Roles || []);
      setScreenMap(normalizeScreens(mine.screens || mine.Screens));
    } catch {
      setIsAdmin(isAdminUser());
      setRoles([]);
      setScreenMap({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bindAuthInterceptor();
    reload();
    const onAuth = () => reload();
    window.addEventListener("storage", onAuth);
    window.addEventListener("sundorbon-auth-updated", onAuth);
    return () => {
      window.removeEventListener("storage", onAuth);
      window.removeEventListener("sundorbon-auth-updated", onAuth);
    };
  }, [reload]);

  const getGrant = useCallback(
    (screenCode) => (screenCode ? screenMap[screenCode] : null),
    [screenMap],
  );

  const canView = useCallback(
    (screenCode) => {
      const grant = getGrant(screenCode);
      if (grant) return !!grant.canView;
      return Object.keys(screenMap).length === 0 && isAdmin;
    },
    [getGrant, isAdmin, screenMap],
  );

  const canCreate = useCallback(
    (screenCode) => {
      const grant = getGrant(screenCode);
      if (grant) return !!grant.canCreate;
      return Object.keys(screenMap).length === 0 && isAdmin;
    },
    [getGrant, isAdmin, screenMap],
  );

  const canEdit = useCallback(
    (screenCode) => {
      const grant = getGrant(screenCode);
      if (grant) return !!grant.canEdit;
      return Object.keys(screenMap).length === 0 && isAdmin;
    },
    [getGrant, isAdmin, screenMap],
  );

  const canDelete = useCallback(
    (screenCode) => {
      const grant = getGrant(screenCode);
      if (grant) return !!grant.canDelete;
      return Object.keys(screenMap).length === 0 && isAdmin;
    },
    [getGrant, isAdmin, screenMap],
  );

  const hasAnyAdminAccess = useMemo(() => {
    if (Object.values(screenMap).some((s) => s.canView)) return true;
    return Object.keys(screenMap).length === 0 && isAdmin;
  }, [isAdmin, screenMap]);

  const firstAllowedPath = useMemo(() => {
    const allowed = Object.values(screenMap).filter((s) => s.canView);
    const dashboard = allowed.find((s) => s.screenCode === "dashboard");
    if (dashboard?.routePath) return dashboard.routePath;
    return allowed[0]?.routePath || "/admin-dashboard";
  }, [screenMap]);

  const value = {
    loading,
    isAdmin,
    roles,
    screenMap,
    canView,
    canCreate,
    canEdit,
    canDelete,
    hasAnyAdminAccess,
    firstAllowedPath,
    reload,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermission() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    return {
      loading: false,
      isAdmin: isAdminUser(),
      roles: [],
      screenMap: {},
      canView: () => isAdminUser(),
      canCreate: () => isAdminUser(),
      canEdit: () => isAdminUser(),
      canDelete: () => isAdminUser(),
      hasAnyAdminAccess: isAdminUser(),
      firstAllowedPath: "/admin-dashboard",
      reload: () => {},
    };
  }
  return ctx;
}
